import React, { useState, useEffect } from 'react';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { Mail, User, CreditCard, Users, Minus, Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useParams, Link } from 'react-router-dom';
import type { Campaign } from '../lib/supabase';
import { createTransactionWithEntries } from '../lib/transactions';
import { toast } from 'react-hot-toast';
import { Entry } from '../types/entry';

const RaffleEntry: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'online' | 'inPerson' | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [numberOfTickets, setNumberOfTickets] = useState(1);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState<Entry | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCampaign();
  }, [eventId]);

  const fetchCampaign = async () => {
    try {
      if (!eventId) {
        console.error('No eventId provided');
        return;
      }

      console.log('Fetching campaign with ID:', eventId);

      // First check if the campaign exists and is active
      const { data: campaignData, error } = await supabase
        .from('campaigns')
        .select('*')
        .eq('id', eventId)
        .eq('status', 'active') // Only fetch active campaigns
        .single();

      if (error) {
        console.error('Supabase error fetching campaign:', error);

        // If the error is because the campaign wasn't found, try fetching without the active filter
        // This helps with debugging by showing if the campaign exists but isn't active
        if (error.code === 'PGRST116') {
          const { data: inactiveCampaign, error: secondError } = await supabase
            .from('campaigns')
            .select('*')
            .eq('id', eventId)
            .single();

          if (!secondError && inactiveCampaign) {
            console.log('Campaign exists but is not active. Status:', inactiveCampaign.status);
            setError(`This raffle is not currently active (Status: ${inactiveCampaign.status})`);
          }
        }

        throw error;
      }

      if (!campaignData) {
        console.error('Campaign not found for ID:', eventId);
      } else {
        console.log('Campaign found:', campaignData.name, 'Status:', campaignData.status);
      }

      setCampaign(campaignData);
    } catch (error) {
      console.error('Error fetching campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load persisted data on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('verifiedEmail');
    const savedName = localStorage.getItem('userName');
    const isVerified = localStorage.getItem('isVerified');

    if (savedEmail) {
      setEmail(savedEmail);
      setVerified(true);
    }
    if (savedName) {
      setUserName(savedName);
    }
    if (isVerified === 'true') {
      setVerified(true);
    }
  }, []);

  const handleEmailVerification = async () => {
    try {
      const currentPath = window.location.pathname + window.location.search;
      const fullRedirectUrl = window.location.origin + currentPath;

      // Use environment variable or fallback to relative URL which works in both dev and prod
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const verificationUrl = `${apiUrl}/verification/send`;

      console.log('Sending verification to:', verificationUrl);
      console.log('Redirect URL:', fullRedirectUrl);

      const response = await fetch(verificationUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          email,
          redirectPath: fullRedirectUrl
        }),
      });

      if (response.ok) {
        setVerificationSent(true);
        setVerificationError(null);
      } else {
        const errorText = await response.text();
        console.error('Verification API error:', errorText);
        throw new Error(`Failed to send verification email: ${errorText}`);
      }
    } catch (error) {
      console.error('Error sending verification email:', error);
      setVerificationError('Failed to send verification email. Please try again.');
    }
  };

  // Handle name persistence
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setUserName(newName);
    localStorage.setItem('userName', newName);
  };

  // Handle ticket quantity changes
  const handleTicketChange = (change: number) => {
    const newValue = numberOfTickets + change;
    if (newValue >= 1) {
      setNumberOfTickets(newValue);
    }
  };

  // Calculate total cost
  const totalCost = (campaign?.ticket_price || 0) * numberOfTickets;

  const handleInPersonEntry = async () => {
    try {
      if (!campaign) {
        toast.error('Campaign not found');
        return;
      }

      if (!email || !userName || numberOfTickets <= 0) {
        toast.error('Please fill in all required fields');
        return;
      }

      // First create an entry
      const { data: entryData, error: entryError } = await supabase
        .from('entries')
        .insert({
          campaign_id: campaign.id,
          email,
          name: userName,
          status: 'active'
        })
        .select()
        .single();

      if (entryError) throw entryError;

      await createTransactionWithEntries({
        campaignId: campaign.id,
        entryId: entryData.id,
        amount: campaign.ticket_price * numberOfTickets,
        paymentMethod: 'inPerson',
        status: 'completed',
        numberOfTickets,
      });

      toast.success('Entry created successfully!');
      setEmail('');
      setUserName('');
      setNumberOfTickets(1);
    } catch (error) {
      console.error('Error creating entry:', error);
      toast.error('Failed to create entry. Please try again.');
    }
  };

  const handlePayPalSuccess = async (details: any) => {
    try {
      if (!campaign || !entry) {
        setError('Campaign or entry not found');
        return;
      }

      const result = await createTransactionWithEntries({
        campaignId: campaign.id,
        entryId: entry.id,
        amount: campaign.ticket_price * numberOfTickets,
        paymentMethod: 'paypal',
        status: 'completed',
        paypalPaymentId: details.id,
        numberOfTickets,
      });

      toast.success('Payment successful! Your tickets have been created.');
      setEmail('');
      setUserName('');
      setNumberOfTickets(1);
    } catch (error) {
      console.error('Error processing PayPal payment:', error);
      setError('Failed to process payment. Please try again.');
    }
  };

  // Check for verification success/error parameters in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verificationSuccess = params.get('verificationSuccess');
    const verificationError = params.get('verificationError');

    if (verificationSuccess === 'true') {
      // Get the email from the URL parameters
      const verifiedEmail = params.get('email');
      if (verifiedEmail) {
        setEmail(verifiedEmail);
        localStorage.setItem('verifiedEmail', verifiedEmail);
      }
      setVerified(true);
      setVerificationSent(false);
      localStorage.setItem('isVerified', 'true');

      // Clean up the URL
      const newUrl = window.location.pathname + window.location.search.replace(/[?&]verificationSuccess=true/, '').replace(/[?&]email=[^&]+/, '');
      window.history.replaceState({}, '', newUrl);
    } else if (verificationError) {
      setVerificationError(decodeURIComponent(verificationError));
      // Clean up the URL
      const newUrl = window.location.pathname + window.location.search.replace(/[?&]verificationError=[^&]+/, '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  if (loading) {
    return (
      <div className="w-full max-w-md mx-auto bg-white rounded-3xl overflow-hidden shadow-xl">
        <div className="p-8 text-center">
          <div className="animate-pulse flex flex-col items-center">
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3 mb-6"></div>
            <p className="text-gray-500">Loading raffle details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="w-full max-w-md mx-auto bg-white rounded-3xl overflow-hidden shadow-xl">
        <div className="p-8 text-center">
          <h2 className="text-xl font-bold text-red-600 mb-4">Raffle not found</h2>
          {error ? (
            <p className="text-gray-600 mb-6">{error}</p>
          ) : (
            <p className="text-gray-600 mb-6">
              The raffle you're looking for might not be active yet or has ended.
            </p>
          )}
          <p className="text-sm text-gray-500 mb-6">
            If you believe this is an error, please contact the raffle organizer.
          </p>
          <Link to="/" className="mt-6 inline-block text-purple-600 hover:text-purple-800 font-medium">
            Return to home page
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto bg-white rounded-3xl overflow-hidden shadow-xl">
      {/* Purple Header Section */}
      <div className="bg-purple-600 p-6">
        <h1 className="text-2xl font-bold text-white mb-2">{campaign.name}</h1>
        <p className="text-purple-100 mb-4">{campaign.description || 'Enter to win!'}</p>
        <div className="flex justify-between items-center text-white text-sm">
          <span>${campaign.ticket_price.toFixed(2)} per ticket</span>
          <span>Ends {new Date(campaign.end_date).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Form Section */}
      <div className="p-6 space-y-6">
        {/* Email Input */}
        <div>
          <label className="flex items-center gap-2 text-gray-700 mb-2">
            <Mail className="h-5 w-5" />
            Email Address
          </label>
          <div className="space-y-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className={`w-full p-3 border rounded-lg focus:outline-none focus:border-purple-500 ${
                verified ? 'border-green-500 bg-green-50' : 'border-gray-200'
              }`}
              disabled={verified}
            />
            {!verified && !verificationSent && email && (
              <button
                type="button"
                onClick={handleEmailVerification}
                className="w-full bg-purple-600 text-white p-3 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
              >
                <Mail className="h-5 w-5" />
                Verify Email
              </button>
            )}
          </div>
        </div>

        {/* Verification Status Messages */}
        {verificationError && (
          <div className="text-red-600 text-sm">
            {verificationError}
          </div>
        )}
        {verificationSent && !verified && (
          <div className="text-purple-600 text-sm">
            Please check your email and click the verification link to continue.
          </div>
        )}

        {/* Name Input */}
        <div>
          <label className="flex items-center gap-2 text-gray-700 mb-2">
            <User className="h-5 w-5" />
            Name (Optional)
          </label>
          <input
            type="text"
            value={userName}
            onChange={handleNameChange}
            placeholder="Enter your name"
            className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
          />
        </div>

        {/* Ticket Quantity Selector - Only shown after email verification */}
        {verified &&
          <div>
            <label className="flex items-center gap-2 text-gray-700 mb-2">
              Number of Tickets
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleTicketChange(-1)}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
                disabled={numberOfTickets <= 1}
              >
                <Minus className="h-5 w-5" />
              </button>
              <input
                type="number"
                value={numberOfTickets}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value >= 1) setNumberOfTickets(value);
                }}
                className="w-20 p-3 text-center border border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
                min="1"
              />
              <button
                type="button"
                onClick={() => handleTicketChange(1)}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-2 text-purple-600 font-medium">
              Total Cost: ${totalCost.toFixed(2)}
              <div className="text-sm text-gray-500">
                {numberOfTickets} ticket{numberOfTickets > 1 ? 's' : ''} × ${campaign.ticket_price.toFixed(2)}
              </div>
            </div>
          </div>
        }

        {/* Payment Method */}
        {verified && (
          <div>
            <label className="block text-gray-700 mb-2">Payment Method</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  console.log('Setting payment method to online');
                  setPaymentMethod('online');
                }}
                className={`p-3 flex justify-center items-center gap-2 rounded-lg border ${
                  paymentMethod === 'online'
                    ? 'bg-white border-gray-200'
                    : 'border-gray-200'
                }`}
              >
                <CreditCard className="h-5 w-5" />
                Pay Online
              </button>
              <button
                type="button"
                onClick={() => {
                  console.log('Setting payment method to person');
                  setPaymentMethod('inPerson');
                }}
                className={`p-3 flex justify-center items-center gap-2 rounded-lg ${
                  paymentMethod === 'inPerson'
                    ? 'bg-purple-600 text-white'
                    : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                } transition-colors`}
              >
                <Users className="h-5 w-5" />
                Pay In Person
              </button>
            </div>
          </div>
        )}

        {/* Terms and Enter Button */}
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            By entering this raffle, you agree to our{' '}
            <a href="#" className="text-purple-600 hover:underline">
              Terms and Conditions
            </a>
          </p>

          <button
            type="button"
            onClick={() => {
              console.log('Button clicked', { verified, paymentMethod, email });
              if (!verified) {
                handleEmailVerification();
              } else if (paymentMethod === 'online') {
                // Handle online payment
              } else if (paymentMethod === 'inPerson') {
                handleInPersonEntry();
              }
            }}
            disabled={!email || !verified || !paymentMethod}
            className={`w-full p-3 rounded-lg transition-colors ${
              !verified
                ? 'bg-purple-200 text-purple-600 hover:bg-purple-300'
                : paymentMethod === 'inPerson'
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-purple-200 text-purple-600 hover:bg-purple-300'
            } disabled:opacity-50 disabled:cursor-not-allowed font-medium`}
          >
            {!verified ? 'Verify Email' : 'Enter Raffle →'}
          </button>
        </div>

        {verified && paymentMethod === 'online' && (
          <div className="mt-4">
            <PayPalButtons
              style={{
                layout: "vertical",
                shape: "rect"
              }}
              createOrder={(data, actions) => {
                if (!actions.order) return Promise.reject('PayPal actions not available');
                return actions.order.create({
                  intent: "CAPTURE",
                  purchase_units: [
                    {
                      amount: {
                        currency_code: "USD",
                        value: totalCost.toFixed(2),
                      },
                    },
                  ],
                });
              }}
              onApprove={handlePayPalSuccess}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default RaffleEntry;